import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useHostState } from '@/hooks/useHostState';
import { useInlineBanner } from '@/context/InlineBannerContext';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { usePortalMode } from '@/hooks/usePortalMode';
import { useTrackOnce } from '@/lib/analytics/useTrackOnce';

import { DashboardHeader } from '@/features/account/DashboardHeader';
import { NotificationsSection } from '@/features/account/NotificationsSection';
import { BookingsCard } from '@/features/account/BookingsCard';
import { ActivitiesSection } from '@/features/account/ActivitiesSection';
import { SavedFeedSection } from '@/features/account/SavedFeedSection';
import { HostCard } from '@/features/account/HostCard';
import { AccountSettingsCard } from '@/features/account/AccountSettingsCard';

export default function CustomerOverview() {
  const { user, isLoading: authLoading } = useAuth();
  const { hostState } = useHostState();
  const { bookingsCount } = usePortalMode();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showBanner } = useInlineBanner();
  const queryClient = useQueryClient();

  const hasBookings = (bookingsCount ?? 0) > 0;

  // Analytics
  useTrackOnce('mypage_opened', { surface: 'my_page_overview' }, 'overview');

  // Show inline banner after interest submission redirect
  useEffect(() => {
    if (!authLoading && user?.id && searchParams.get('interest') === '1') {
      queryClient.invalidateQueries({ queryKey: ['host-state', user.id] });
      showBanner({ message: 'Vi har tagit emot din intresseanmälan.', variant: 'success', durationMs: 3000 });
      searchParams.delete('interest');
      setSearchParams(searchParams, { replace: true });
    }
  }, [authLoading, user?.id]);

  // One-time welcome banner for new hosts (onboarded_at IS NULL)
  useEffect(() => {
    if (hostState.kind !== 'active_host') return;
    if (hostState.onboardedAt !== null) return;

    supabase
      .from('producer_members')
      .update({ onboarded_at: new Date().toISOString() })
      .eq('id', hostState.membershipId)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['host-state', user?.id] });
      });

    showBanner({ message: 'Välkommen som värd i Farmly! 🌿', variant: 'success', durationMs: 6000 });
  }, [hostState.kind]);

  return (
    <div className="w-full max-w-full overflow-x-clip">
      <div className="mx-auto w-full max-w-5xl px-4 pt-8 pb-24">

        {/* ─── 1. Header ─── */}
        <DashboardHeader />

        {/* ─── Responsive grid: stacked on mobile, 2-col on md+ ─── */}
        <div className="mt-8 flex flex-col md:grid md:grid-cols-12 md:gap-8 gap-8">

          {/* ─── Left column (primary content) ─── */}
          <div className="md:col-span-8 lg:col-span-8 space-y-8">

            {/* 1. Nytt från sparat */}
            <SavedFeedSection />

            {/* 2. Det händer i Dalarna */}
            <ActivitiesSection />

            {/* 3. Meddelanden (kompakt) */}
            <NotificationsSection />
          </div>

          {/* ─── Right column (secondary cards) ─── */}
          <div className="md:col-span-4 lg:col-span-4 space-y-8">
            {/* 3. Dina bokningar — only if user has bookings */}
            {hasBookings && (
              <section>
                <h2 className="text-sm font-medium text-muted-foreground/70 mb-3">
                  Dina bokningar
                </h2>
                <BookingsCard />
              </section>
            )}

            {/* 4. Din plats — for hosts */}
            {hostState.kind !== 'not_host' && <HostCard />}

            {/* Konto */}
            <section>
              <AccountSettingsCard />
            </section>
          </div>

        </div>
      </div>
    </div>
  );
}
